<template>
  <div class="tableArea">
    <table class="table table-striped">
      <thead>
        <tr>
          <th>&nbsp;</th>
          <th colspan="20">{{ tableheader }}</th>
        </tr>
        <tr v-if="tablerows.length > 0">
           <th v-for="(col, index) in tablerows[0].rowdata"
               :key="name + '-header-row-col-' + index"
               class="header-data-cell">
               <template v-if="index === 0">&nbsp;</template>
               <template v-else>{{ col }}</template>
           </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, rowIndex) in tablerows.slice(1)"
          :key="name + '-row-' + (rowIndex + 1)"
          :id="name + '-row-' + (rowIndex + 1)">
        <td v-for="(col, index) in row.rowdata"
            :key="name + '-' + (rowIndex + 1) + '-td-' + index"
            :id="name + '-' + (rowIndex + 1) + '-td-' + index">
            <!-- First column has circle/label -->
            <template v-if="index === 0"><div class="circle" :class="getCircleClass(col)">&nbsp;</div>{{ col }}</template>
            <!-- Other columns formatted number -->
            <template v-else>{{ formatTableNumber(col) }}</template>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
  <p class="mobile-scroll-hint">
    <font-awesome-icon icon="arrows-left-right" /> Swipe/scroll horizontally on the table to see more data
  </p>
</template>

<script setup lang="ts">
defineProps<{
  name: string
  tableheader: string
  tablerows: { rowdata: (string | number)[] }[]
  formatTableNumber: (value: string | number) => string
}>()

const getCircleClass = (val: string | number) => {
  const text = String(val).toLowerCase()
  if (text.includes('top')) return 'circle-top'
  if (text.includes('bottom') || text.includes('lower')) return 'circle-bottom'
  if (text.includes('median')) return 'circle-median'
  return ''
}
</script>

<style lang="scss" scoped>
    // Table styles
    .tableArea {
      overflow-x: auto;
      overflow-y: hidden;
      margin: 20px 0;
      scrollbar-color: rebeccapurple var(--background-colour);
      height: auto;
    }

    table {
      border-collapse: separate;
      border-spacing: 0;
      width: 100%;
    }
    tr {
      background-color: var(--background-colour);
    }


    tr {
        td:first-child, th:first-child {
            display: flex;
            flex-direction: row-reverse;
            align-items: center;
            border: none;
            text-align: right;
            padding-right: 10px;
            min-width: 200px;
        }
        td, th {
            border-bottom: 2px solid var(--lcp-blue);
            padding-left: 4px;
            padding-right: 4px;
            text-align: left;
            padding-top: 6px;
            padding-bottom: 6px;
            font-size: 0.9rem;
        }
    }

        // Circle indicators
    @mixin circle($color) {
      float: right;
      background: $color;
      width: 14px;
      height: 14px;
      border-radius: 100px;
      margin: 2px 2px 2px 10px;
    }

    /*
       Original Row 1 (Index 1) had Cyan.
       Now it is Body Row 1 (Index 0).
       nth-child(1) matches the first row of tbody.
    */
    tbody tr:nth-child(1) td .circle { @include circle(rgb(0, 165, 201)); }
    tbody tr:nth-child(2) td .circle { @include circle(rgb(251, 166, 28)); }
    tbody tr:nth-child(3) td .circle { @include circle(rgb(23, 53, 94)); }

    /* Overrides based on content class */
    tbody tr td .circle.circle-top { @include circle(rgb(0, 165, 201)); }
    tbody tr td .circle.circle-median { @include circle(rgb(251, 166, 28)); }
    tbody tr td .circle.circle-bottom { @include circle(rgb(23, 53, 94)); }

        /* Sticky for body rows first column */
        tbody tr td:first-child {
            position: sticky;
            left: 0;
            z-index: 10;
            background-color: var(--background-colour);
            min-width: auto;
            width: auto;
            border-bottom: 2px solid var(--lcp-blue);
            white-space: nowrap;
            background-clip: padding-box;
        }

        /* Sticky for header first cell */
        thead tr th:first-child {
            position: sticky;
            left: 0;
            z-index: 10;
            background-color: var(--background-colour);
            min-width: auto;
            width: auto;
            border-bottom: 2px solid var(--background-colour);
        }

    .mobile-scroll-hint {
        display: none;
        text-align: center;
        margin-top: -15px; /* Pull it slightly closer to the table */
        margin-bottom: 20px;
        font-size: 0.8rem;
        color: var(--lcp-blue);
        font-style: italic;

        svg {
            margin-right: 5px;
        }
    }

    /* Target mobile devices and tablets where scrolling is necessary */
    @media only screen and (max-width: 1024px) {
        .mobile-scroll-hint {
            display: block;
        }
    }
</style>
